// ExamHistoryListing.tsx

import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Avatar,
  useTheme,
  TablePagination,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  alpha,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Timer as TimerIcon,
  AutoAwesome as AutoAwesomeIcon,
  Percent as PercentIcon,
  Close,
} from "@mui/icons-material";
import ReactECharts from "echarts-for-react";
import { getLocalizedString, type QuizSubmissionResponse } from "@/types/Questions.types";
import quizApi from "@/services/quizService";
import { ExamResultPage } from "./ExamResultPage";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const PREFIX = "pages.examHistoryListing";

export const ExamHistoryListing: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [submissions, setSubmissions] = useState<QuizSubmissionResponse[]>([]);
  const [quizResultDict, setQuizResultDict] = useState<{ quizName: string; submissionId: string } | undefined>(undefined);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    quizApi.getAllSubmissions().then((data) => {
      setSubmissions(data || []);
    });
  }, []);

  // Combined Multi-Filter Engine (Search Query + Date Boundaries)
  const filteredSubmissions = submissions.filter((sub) => {
    const title = getLocalizedString(sub.quiz.name).toLowerCase();
    const id = sub.submissionId.toLowerCase();
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch = title.includes(term) || id.includes(term);

    if (!matchesSearch) return false;

    // Date calculations
    if (startDate || endDate) {
      const targetDate = new Date(sub.createdAt).getTime();
      if (startDate && targetDate < new Date(startDate).setHours(0, 0, 0, 0)) return false;
      if (endDate && targetDate > new Date(endDate).setHours(23, 59, 59, 999)) return false;
    }

    return true;
  });

  // Calculate Paginated Set
  const paginatedSubmissions = filteredSubmissions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatTime = (seconds: number) => {
    if (seconds === 0) return "0s";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  // Re-evaluating Analytics against the CURRENT FILTERED dataset
  const filteredTotalCount = filteredSubmissions.length;

  const filteredAccuracyRate = (() => {
    let globalCorrect = 0;
    let globalTotalQuestions = 0;

    filteredSubmissions.forEach((sub) => {
      const correctAnswers = sub.responses?.filter((r) => r.isCorrect).length || 0;
      globalCorrect += correctAnswers;
      globalTotalQuestions += sub.quiz.totalQuestions || 0;
    });

    return globalTotalQuestions > 0 ? (globalCorrect / globalTotalQuestions) * 100 : 0;
  })();

  const getMotivationalMessage = () => {
    if (filteredTotalCount === 0) {
      return {
        title: t(`${PREFIX}.motivation.adjustFilters.title`),
        desc: t(`${PREFIX}.motivation.adjustFilters.desc`),
      };
    }

    // Round to ensure we get an exact integer between 0 and 100
    const rate = Math.min(100, Math.max(0, Math.round(filteredAccuracyRate)));

    return {
      title: t(`${PREFIX}.motivation.rates.${rate}.title`),
      desc: t(`${PREFIX}.motivation.rates.${rate}.desc`),
    };
  };

  const motivation = getMotivationalMessage();

  // ECharts Logic tracking filtered items (Theme-aware tooltips and axes)
  const getChartOptions = () => {
    const timelineData = [...filteredSubmissions]
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .reduce((acc: Record<string, number>, curr) => {
        const dateStr = new Date(curr.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        acc[dateStr] = (acc[dateStr] || 0) + 1;
        return acc;
      }, {});

    return {
      tooltip: {
        trigger: "axis",
        backgroundColor: theme.palette.background.paper,
        textStyle: { color: theme.palette.text.primary },
        borderColor: theme.palette.divider,
        borderWidth: 1,
      },
      grid: { left: "4%", right: "4%", bottom: "12%", top: "15%", containLabel: true },
      xAxis: {
        type: "category",
        data: Object.keys(timelineData),
        axisLine: { lineStyle: { color: theme.palette.divider } },
        axisLabel: { color: theme.palette.text.secondary },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        splitLine: { lineStyle: { type: "dashed", color: theme.palette.divider } },
        axisLabel: { color: theme.palette.text.secondary },
      },
      series: [
        {
          name: t(`${PREFIX}.cards.chartSeriesSubmissions`),
          data: Object.values(timelineData),
          type: "line",
          smooth: true,
          symbolSize: 8,
          itemStyle: { color: theme.palette.primary.main },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: alpha(theme.palette.primary.main, 0.3) },
                { offset: 1, color: alpha(theme.palette.primary.main, 0.0) },
              ],
            },
          },
        },
      ],
    };
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pt: { xs: 4, md: 8 }, bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Top Banner Widget */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
            {t(`${PREFIX}.title`)}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {t(`${PREFIX}.subtitle`)}
          </Typography>
        </Box>

        {/* Gamification Card Component */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            bgcolor: "background.paper",
            p: 2.5,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
            maxWidth: 420,
          }}
        >
          <Avatar
            sx={{
              bgcolor: "secondary.main",
              color: "secondary.contrastText",
              width: 46,
              height: 46,
              boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
            }}
          >
            <AutoAwesomeIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "text.primary", mb: 0.5 }}>
              {motivation.title}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.3, fontWeight: 500, color: "text.secondary" }}>
              {motivation.desc}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Analytics & Graphs Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }} container spacing={2}>
          {/* Synchronized Total Counter Card */}
          <Grid size={{ xs: 12 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: "background.paper",
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", p: 2.5 }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    mr: 2,
                    width: 48,
                    height: 48,
                  }}
                >
                  <AssignmentIcon />
                </Avatar>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "0.5px", color: "text.secondary" }}>
                    {t(`${PREFIX}.cards.filteredAttempts`)}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
                    {filteredTotalCount}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Synchronized Accuracy Rate Card */}
          <Grid size={{ xs: 12 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: "background.paper",
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", p: 2.5 }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    color: theme.palette.info.main,
                    mr: 2,
                    width: 48,
                    height: 48,
                  }}
                >
                  <PercentIcon />
                </Avatar>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "0.5px", color: "text.secondary" }}>
                    {t(`${PREFIX}.cards.avgAccuracyRate`)}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.info.main }}>
                    {filteredAccuracyRate.toFixed(1)}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ECharts Wrapper Chart */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary", px: 2, pt: 0.5 }}>
                {t(`${PREFIX}.cards.submissionVelocityTrend`)}
              </Typography>
              {filteredTotalCount > 0 ? (
                <ReactECharts option={getChartOptions()} style={{ height: "145px", width: "100%" }} />
              ) : (
                <Box sx={{ height: "145px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t(`${PREFIX}.cards.noMetricsMatch`)}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table Paper Layout */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 1,
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
        }}
      >
        {/* Controls strip: Search + Date Range Pickers */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
            justifyContent: "space-between",
          }}
        >
          <TextField
            size="small"
            placeholder={t(`${PREFIX}.filters.searchPlaceholder`)}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ flexGrow: 1, maxWidth: { sm: 1024 }, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
          />

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <TextField
              size="small"
              type="date"
              label={t(`${PREFIX}.filters.startDate`)}
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(0);
              }}
              slotProps={{
                inputLabel: { shrink: true },
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
            />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t(`${PREFIX}.filters.to`)}
            </Typography>
            <TextField
              size="small"
              type="date"
              label={t(`${PREFIX}.filters.endDate`)}
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(0);
              }}
              slotProps={{
                inputLabel: { shrink: true },
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
            />
          </Stack>
        </Stack>

        <TableContainer>
          <Table sx={{ minWidth: 950 }}>
            <TableHead sx={{ bgcolor: alpha(theme.palette.text.primary, 0.03) }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>
                  {t(`${PREFIX}.table.headers.quizDetails`)}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>
                  {t(`${PREFIX}.table.headers.submittedOn`)}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>
                  {t(`${PREFIX}.table.headers.accuracyScore`)}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>{t(`${PREFIX}.table.headers.coverage`)}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>{t(`${PREFIX}.table.headers.duration`)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "text.secondary", pr: 3 }}>
                  {t(`${PREFIX}.table.headers.actions`)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSubmissions.map((row) => {
                const totalQuestions = row.quiz.totalQuestions || 1;
                const answeredCount = row.responses?.length || 0;
                const correctCount = row.responses?.filter((r) => r.isCorrect).length || 0;
                const accuracyPercent = (correctCount / totalQuestions) * 100;

                return (
                  <TableRow
                    key={row.submissionId}
                    sx={{
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        transition: "background-color 0.15s ease",
                      },
                    }}
                  >
                    {/* Title */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                        {getLocalizedString(row.quiz.paper)}
                      </Typography>
                    </TableCell>

                    {/* Timestamp */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                        {new Date(row.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </TableCell>

                    {/* Accurate Correct Metrics */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: accuracyPercent >= 70 ? (isDark ? "#4ade80" : "#16a34a") : isDark ? "#f87171" : "#dc2626",
                          }}
                        >
                          {accuracyPercent.toFixed(0)}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {t(`${PREFIX}.table.correctText`, { correct: correctCount, total: totalQuestions })}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Coverage Count */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                        {t(`${PREFIX}.table.qsText`, { answered: answeredCount, total: totalQuestions })}
                      </Typography>
                    </TableCell>

                    {/* Duration */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                        <TimerIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.7 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatTime(row.totalTimeConsumed)}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Individual Row Actions */}
                    <TableCell align="right" sx={{ pr: 3 }}>
                      <Tooltip title={t(`${PREFIX}.tooltip.viewDetails`)} arrow>
                        <IconButton
                          color="primary"
                          onClick={() =>
                            setQuizResultDict({ quizName: row.quiz.name.toString(), submissionId: row.submissionId })
                          }
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                            borderRadius: 1,
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}

              {filteredSubmissions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {t(`${PREFIX}.table.noRecords`)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredSubmissions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: `1px solid ${theme.palette.divider}`, bgcolor: "background.paper" }}
        />
      </Paper>

      {quizResultDict ? (
        <Dialog open={!!quizResultDict} onClose={() => setQuizResultDict(undefined)} maxWidth="md" fullWidth>
          {/* Dialog Header */}
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "background.paper",
              color: "text.primary",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {t(`${PREFIX}.dialog.title`)}
            </Typography>
            <IconButton onClick={() => setQuizResultDict(undefined)} aria-label="close">
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 3, bgcolor: "background.default" }}>
            <ExamResultPage quizNameProp={quizResultDict.quizName} submissionIdProp={quizResultDict.submissionId} />
          </DialogContent>

          <DialogActions sx={{ p: 2, bgcolor: "background.paper" }}>
            <Button onClick={() => setQuizResultDict(undefined)} variant="outlined" color="primary">
              {t(`${PREFIX}.dialog.close`)}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </Box>
  );
};
